import { EventType, NativeInteractionType, } from '@replayops/rrweb-schema';
import { buildTimeline } from './timeline.js';
/**
 * Replay engine for native (iOS / Android) sessions.
 *
 * A native recording is a sequence of rendered frames plus the gestures between
 * them — there is no DOM to reconstruct, so rrweb's Replayer has nothing to do
 * here. This mirrors {@link ReplayEngine}'s control surface (play/pause/seek/
 * speed and the same {@link ReplayTimeline}) so the player UI can drive either
 * one without caring which it has.
 *
 * Because every frame is self-contained, seeking is exact and cheap: find the
 * last frame at or before the target time and draw it. There is no keyframe to
 * rebuild from and no way for a dropped batch to corrupt what comes after.
 */
/** A touch drawn over the frame for this long after it happened. */
const TOUCH_FADE_MS = 700;
export class NativeReplayEngine {
    timeline;
    frames = [];
    touches = [];
    canvas;
    images = new Map();
    listeners = new Set();
    speed = 1;
    playing = false;
    raf = 0;
    /** Playhead in ms from session start. */
    position = 0;
    /** Wall-clock anchor for the current play run, so speed changes are exact. */
    startedAt = 0;
    startedFrom = 0;
    drawnFrame = -1;
    constructor(events, root) {
        this.timeline = buildTimeline(events);
        const start = events.length ? Math.min(...events.map((e) => e.timestamp)) : 0;
        for (const e of events) {
            if (e.type === EventType.NativeFrame) {
                this.frames.push({ offsetMs: e.timestamp - start, data: e.data });
            }
            else if (e.type === EventType.NativeInteraction) {
                const d = e.data;
                // Screen changes are timeline markers, not something to draw.
                if (d.kind !== NativeInteractionType.ScreenChange) {
                    this.touches.push({ offsetMs: e.timestamp - start, data: d });
                }
            }
        }
        this.frames.sort((a, b) => a.offsetMs - b.offsetMs);
        this.touches.sort((a, b) => a.offsetMs - b.offsetMs);
        this.canvas = document.createElement('canvas');
        this.canvas.style.cssText = 'width:100%;height:100%;object-fit:contain;display:block';
        const first = this.frames[0]?.data;
        this.canvas.width = first?.width ?? 390;
        this.canvas.height = first?.height ?? 844;
        root.appendChild(this.canvas);
        this.preload();
        this.render();
    }
    /** Native frames are their own dimensions — the stage sizes itself to this. */
    getStageSize() {
        return { width: this.canvas.width, height: this.canvas.height };
    }
    getTimeline() {
        return this.timeline;
    }
    play(atMs) {
        const from = Math.max(0, Math.min(atMs ?? this.position, this.timeline.durationMs));
        if (from >= this.timeline.durationMs)
            return; // already at the end
        this.position = from;
        this.startedFrom = from;
        this.startedAt = performance.now();
        this.playing = true;
        this.tick();
        this.emit();
    }
    pause() {
        this.playing = false;
        cancelAnimationFrame(this.raf);
        this.emit();
    }
    toggle() {
        this.playing ? this.pause() : this.play();
    }
    seek(offsetMs) {
        this.position = Math.max(0, Math.min(offsetMs, this.timeline.durationMs));
        this.startedFrom = this.position;
        this.startedAt = performance.now();
        this.render();
        this.emit();
    }
    jumpToMarker(index) {
        const marker = this.timeline.markers[index];
        if (marker)
            this.seek(marker.offsetMs);
    }
    setSpeed(speed) {
        // Re-anchor so the elapsed time already played isn't rescaled.
        this.startedFrom = this.position;
        this.startedAt = performance.now();
        this.speed = speed;
        this.emit();
    }
    on(listener) {
        this.listeners.add(listener);
        listener(this.state());
        return () => this.listeners.delete(listener);
    }
    destroy() {
        cancelAnimationFrame(this.raf);
        this.playing = false;
        this.listeners.clear();
        this.images.clear();
        this.canvas.remove();
    }
    // ── internals ──
    /**
     * Decodes every frame up front. A native recording is a few hundred small
     * images at most (capture is change-driven, not a fixed frame rate), and
     * decoding on demand would stall playback at exactly the wrong moment.
     */
    preload() {
        this.frames.forEach((f, i) => {
            const img = new Image();
            img.onload = () => {
                // The first decoded frame is what the stage sizes itself from.
                if (i === this.frameIndexAt(this.position))
                    this.render();
            };
            img.src = f.data.image;
            this.images.set(i, img);
        });
    }
    /** Index of the last frame at or before `offsetMs`, or -1 before the first. */
    frameIndexAt(offsetMs) {
        let lo = 0;
        let hi = this.frames.length - 1;
        let found = -1;
        while (lo <= hi) {
            const mid = (lo + hi) >> 1;
            if (this.frames[mid].offsetMs <= offsetMs) {
                found = mid;
                lo = mid + 1;
            }
            else {
                hi = mid - 1;
            }
        }
        return found;
    }
    render() {
        const ctx = this.canvas.getContext('2d');
        if (!ctx)
            return;
        const idx = this.frameIndexAt(this.position);
        const frame = idx >= 0 ? this.frames[idx] : this.frames[0];
        if (!frame)
            return;
        if (this.canvas.width !== frame.data.width || this.canvas.height !== frame.data.height) {
            this.canvas.width = frame.data.width;
            this.canvas.height = frame.data.height;
            this.drawnFrame = -1;
        }
        const img = this.images.get(idx >= 0 ? idx : 0);
        ctx.fillStyle = '#000';
        ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
        if (img?.complete && img.naturalWidth > 0) {
            ctx.drawImage(img, 0, 0, this.canvas.width, this.canvas.height);
        }
        this.drawnFrame = idx;
        this.drawTouches(ctx);
    }
    /** Recent touches, drawn as fading rings the way a screen recorder would. */
    drawTouches(ctx) {
        for (const t of this.touches) {
            const age = this.position - t.offsetMs;
            if (age < 0 || age > TOUCH_FADE_MS)
                continue;
            if (t.data.x == null || t.data.y == null)
                continue;
            const fade = 1 - age / TOUCH_FADE_MS;
            if (t.data.toX != null && t.data.toY != null) {
                ctx.strokeStyle = `rgba(225,29,72,${fade})`;
                ctx.lineWidth = 4;
                ctx.beginPath();
                ctx.moveTo(t.data.x, t.data.y);
                ctx.lineTo(t.data.toX, t.data.toY);
                ctx.stroke();
            }
            const radius = 18 + (1 - fade) * 22;
            ctx.beginPath();
            ctx.arc(t.data.x, t.data.y, radius, 0, Math.PI * 2);
            ctx.fillStyle = `rgba(225,29,72,${0.25 * fade})`;
            ctx.fill();
            ctx.strokeStyle = `rgba(225,29,72,${fade})`;
            ctx.lineWidth = 3;
            ctx.stroke();
        }
    }
    tick = () => {
        if (!this.playing)
            return;
        const elapsed = (performance.now() - this.startedAt) * this.speed;
        this.position = this.startedFrom + elapsed;
        if (this.position >= this.timeline.durationMs) {
            this.position = this.timeline.durationMs;
            this.playing = false;
            this.render();
            this.emit();
            return;
        }
        // Touch rings animate, so redraw whenever one is still fading — otherwise
        // only when the frame actually changes.
        const idx = this.frameIndexAt(this.position);
        const fading = this.touches.some((t) => {
            const age = this.position - t.offsetMs;
            return age >= 0 && age <= TOUCH_FADE_MS;
        });
        if (idx !== this.drawnFrame || fading)
            this.render();
        // Canvas rendering stays at rAF rate above; listener (React) updates are
        // throttled — at 60/s the host page re-renders every frame and stutters.
        const now = performance.now();
        if (now - this.lastEmitAt >= 100) {
            this.lastEmitAt = now;
            this.emit();
        }
        this.raf = requestAnimationFrame(this.tick);
    };
    /** Last listener notification, for throttling the playback ticker. */
    lastEmitAt = 0;
    state() {
        return {
            playing: this.playing,
            currentTimeMs: Math.max(0, this.position),
            durationMs: this.timeline.durationMs,
            speed: this.speed,
        };
    }
    emit() {
        const s = this.state();
        for (const l of this.listeners)
            l(s);
    }
}
//# sourceMappingURL=nativeEngine.js.map